import React, { useEffect, useContext } from 'react';
import LayoutContainer from 'components/layout/LayoutContainer';
import AdminHeader from 'components/header/index';
import Card from 'components/inputs/Card';
import { Row, Col } from 'react-bootstrap';
import TextInput from 'components/inputs/Input';
import Select from 'components/inputs/Select';
import Button from 'components/inputs/Button';
import * as _ from 'lodash';
import { Formik, Form, FieldArray, getIn } from 'formik';
import * as Yup from 'yup';
import { useSelector, useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { NavContext } from 'components/privateRoute/PrivateRoute';
import { CREATE_STATUS_CODE, UPDATE_STATUS_CODE, MANAGE_SESSION_LEVEL_OFFERS } from 'components/common/constant';
import { CREATE_SESSION_LEVEL_OFFER, UPDATE_SESSION_LEVEL_OFFER, GET_SESSION_LEVEL_OFFER } from 'actions/sessionLevelOffer';
import { FETCH_CHARGING_STATION } from 'actions/chargingStation';
import { getOfferId } from './SessionLevelOfferList';

const toDateTimeInput = (d) => (d ? moment(d).format('YYYY-MM-DDTHH:mm') : '');
const toNumberOrNull = (v) => (v === '' || v === null || v === undefined ? null : Number(v));
const blankToNull = (v, original) => (original === '' ? null : v);
// blank -> undefined so `required` shows its own message instead of a type error
const numberField = () =>
  Yup.number()
    .transform((v, original) => (original === '' ? undefined : v))
    .typeError('Enter a number');
const DISCOUNT_TYPE_OPTIONS = [
  { label: 'Percentage', value: 'percentage' },
  { label: 'Flat (₹)', value: 'flat' },
];
const OFFER_TYPE_OPTIONS = [
  { label: 'Price range', value: 'price_range' },
  { label: 'Session sequence (1st session, 2nd session...)', value: 'session_sequence' },
];
// per offer type: form array, row label, price columns, empty row
const SLABS = {
  price_range: {
    name: 'price_ranges',
    label: 'Slab',
    prices: ['min_price', 'max_price'],
    empty: { min_price: '', max_price: '', discount_type: 'percentage', discount_value: '' },
  },
  session_sequence: {
    name: 'session_cashback_distribution',
    label: 'Session',
    prices: [],
    empty: { discount_type: 'percentage', discount_value: '' },
  },
};
const PRICE_LABELS = { min_price: 'Min Price (₹)', max_price: 'Max Price (₹)' };
const fieldError = (errors, touched, name) => (getIn(errors, name) && getIn(touched, name) ? getIn(errors, name) : null);
const PLACEHOLDER_HELP = 'Placeholders: {{discount_value}} {{discount_type}} {{min_amount}}';
const slabCard = { border: '1px solid #2a3a48', borderRadius: 8, padding: 16, marginBottom: 16 };
const banner = {
  padding: '10px 14px',
  marginBottom: 16,
  borderRadius: 6,
  background: 'rgba(245,96,76,0.08)',
  border: '1px solid #2a3a48',
  fontSize: 13,
};

const validationSchema = Yup.object().shape({
  title: Yup.string().required('Title is required'),
  sub_title: Yup.string().required('Sub title is required'),
  stations: Yup.array().min(1, 'Select at least one station'),
  session_level_max_discount: numberField().transform(blankToNull).min(0, 'Cannot be negative').nullable(),
  user_level_max_discount: numberField().transform(blankToNull).min(0, 'Cannot be negative').nullable(),
  min_units_consumed: numberField().transform(blankToNull).min(0, 'Cannot be negative').nullable(),
  before_apply: Yup.string().required('Content before apply is required'),
  after_apply: Yup.string().required('Content after apply is required'),
  start_date: Yup.date().required('Start date is required'),
  end_date: Yup.date()
    .transform(blankToNull)
    .nullable()
    .test('after-start', 'End date must be after the start date', function (v) {
      return !v || !this.parent.start_date || v > this.parent.start_date;
    }),
  price_ranges: Yup.array().when('offer_type', {
    is: 'price_range',
    then: (schema) =>
      schema.min(1, 'Add at least one slab').of(
        Yup.object().shape({
          min_price: numberField().required('Required'),
          max_price: numberField().required('Required'),
          discount_type: Yup.string().required('Required'),
          discount_value: numberField().required('Required'),
        })
      ),
  }),
  session_cashback_distribution: Yup.array().when('offer_type', {
    is: 'session_sequence',
    then: (schema) =>
      schema.min(1, 'Add at least one slab').of(
        Yup.object().shape({
          discount_type: Yup.string().required('Required'),
          discount_value: numberField().min(1, 'Must be at least 1').required('Required'),
        })
      ),
  }),
});

const SessionLevelOfferForm = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { offerId } = useParams();
  const { navigateTo } = useContext(NavContext);
  const isEdit = !!offerId;

  const detail = useSelector((state) => state.sessionLevelOffer.sessionLevelOfferDetail);
  const allStations = useSelector((state) => state.chargingStation.chargingStations);
  // ignore a stale detail left over from a previously opened offer
  const offer = isEdit && getOfferId(detail) === offerId ? detail : {};
  const rolePermissionData = useSelector((state) => state.manageRole.manageRoles);
  const isReadOnly = isEdit && !_.some(_.get(rolePermissionData, '[0].permissions', []), { name: MANAGE_SESSION_LEVEL_OFFERS });

  useEffect(() => {
    dispatch({ type: FETCH_CHARGING_STATION.REQUEST, payload: { request_all: true } });
    if (isEdit) {
      dispatch({ type: GET_SESSION_LEVEL_OFFER.REQUEST, payload: offerId });
    }
  }, [offerId]);

  const stationOptions = _.map(_.uniqBy([..._.get(offer, 'stations', []), ...(Array.isArray(allStations) ? allStations : [])], getOfferId), (s) => ({
    label: s.name,
    value: getOfferId(s),
  }));

  const initialValues = {
    title: _.get(offer, 'title', ''),
    sub_title: _.get(offer, 'sub_title', ''),
    description: _.get(offer, 'description', ''),
    stations: _.map(_.get(offer, 'stations', []), (s) => (_.isObject(s) ? getOfferId(s) : s)),
    session_level_max_discount: _.get(offer, 'session_level_max_discount') ?? '',
    user_level_max_discount: _.get(offer, 'user_level_max_discount') ?? '',
    min_units_consumed: _.get(offer, 'min_units_consumed', 0),
    discount_form: _.get(offer, 'discount_form', 'cashback'),
    before_apply: _.get(offer, 'content.before_apply', ''),
    after_apply: _.get(offer, 'content.after_apply', ''),
    start_date: toDateTimeInput(_.get(offer, 'start_date')),
    end_date: toDateTimeInput(_.get(offer, 'end_date')),
    offer_type: _.isEmpty(_.get(offer, 'session_cashback_distribution')) ? 'price_range' : 'session_sequence',
  };
  _.forEach(SLABS, ({ name, empty }) => {
    initialValues[name] = _.isEmpty(_.get(offer, name)) ? [empty] : _.map(offer[name], (r) => _.pick(r, _.keys(empty)));
  });

  const onSubmit = (values) => {
    const payload = {
      ..._.pick(values, ['title', 'sub_title', 'description', 'stations', 'discount_form']),
      session_level_max_discount: toNumberOrNull(values.session_level_max_discount),
      user_level_max_discount: toNumberOrNull(values.user_level_max_discount),
      min_units_consumed: Number(values.min_units_consumed) || 0,
      content: { before_apply: values.before_apply, after_apply: values.after_apply },
      start_date: isEdit && values.start_date === toDateTimeInput(offer.start_date) ? offer.start_date : moment(values.start_date).toISOString(),
      end_date: values.end_date ? moment(values.end_date).toISOString() : null,
      ...(isEdit && { id: offerId }),
    };
    // send both arrays so switching type clears the unused one
    _.forEach(SLABS, ({ name }, type) => {
      payload[name] = type === values.offer_type ? _.map(values[name], (r) => _.mapValues(r, (v, k) => (k === 'discount_type' ? v : Number(v)))) : [];
    });
    // keeps isSubmitting true until the saga cb fires
    return new Promise((resolve) => {
      dispatch({
        type: isEdit ? UPDATE_SESSION_LEVEL_OFFER.REQUEST : CREATE_SESSION_LEVEL_OFFER.REQUEST,
        payload,
        cb: (res) => {
          resolve();
          _.get(res, 'status') === (isEdit ? UPDATE_STATUS_CODE : CREATE_STATUS_CODE) && navigateTo('/sessionLevelOffers');
        },
      });
    });
  };

  return (
    <React.Fragment>
      <LayoutContainer>
        <AdminHeader
          title={isEdit ? (isReadOnly ? 'View Session Level Offer' : 'Edit Session Level Offer') : 'Add Session Level Offer'}
          backTo="/sessionLevelOffers"
        />
        <div className="page-content-wrapper scrollable">
          <Card>
            <div className="p-4">
              <Formik enableReinitialize initialValues={initialValues} validationSchema={validationSchema} onSubmit={onSubmit}>
                {({ values, errors, touched, handleChange, handleSubmit, setFieldValue, isSubmitting }) => {
                  const input = (name, label, props = {}) => (
                    <TextInput
                      label={label}
                      name={name}
                      value={getIn(values, name)}
                      onChange={handleChange}
                      error={fieldError(errors, touched, name)}
                      {...props}
                    />
                  );
                  const slabs = SLABS[values.offer_type];
                  return (
                    <Form onSubmit={handleSubmit}>
                      {_.get(offer, 'archived_at') && !isReadOnly && <div style={banner}>This offer is archived. Saving clears the archive.</div>}
                      <fieldset disabled={isReadOnly}>
                        <div className="section-heading mb-3">Offer details</div>
                        <Row>
                          <Col lg={6}>{input('title', 'Title', { isRequired: true })}</Col>
                          <Col lg={6}>{input('sub_title', 'Sub Title', { isRequired: true })}</Col>
                          <Col lg={12}>
                            <Select
                              isRequired
                              isMulti
                              label="Stations"
                              placeholder="Select stations"
                              options={stationOptions}
                              name="stations"
                              value={values.stations}
                              onChange={(val) => setFieldValue('stations', val || [])}
                              isDisabled={isReadOnly}
                              error={fieldError(errors, touched, 'stations')}
                            />
                          </Col>
                          <Col lg={6}>{input('start_date', 'Start Date', { isRequired: true, type: 'datetime-local' })}</Col>
                          <Col lg={6}>
                            {input('end_date', 'End Date (Optional, blank = no end)', {
                              type: 'datetime-local',
                              min: values.start_date || undefined,
                            })}
                          </Col>
                          <Col lg={12}>{input('description', 'Description (Optional)', { as: 'textarea', rows: 3 })}</Col>
                        </Row>

                        <div className="section-heading mb-3">Cashback rules</div>
                        <Row>
                          <Col lg={3}>
                            <Select
                              label="Offer Type"
                              options={OFFER_TYPE_OPTIONS}
                              disableSort
                              name="offer_type"
                              value={values.offer_type}
                              onChange={(val) => setFieldValue('offer_type', val)}
                              isDisabled={isReadOnly}
                            />
                          </Col>
                          <Col lg={3}>{input('session_level_max_discount', 'Max Discount per Session (₹) (Optional)', { type: 'number' })}</Col>
                          <Col lg={3}>{input('user_level_max_discount', 'Max Discount per User (₹) (Optional)', { type: 'number' })}</Col>
                          <Col lg={3}>{input('min_units_consumed', 'Min Units Consumed (kWh) (Optional)', { type: 'number' })}</Col>
                        </Row>
                        <FieldArray key={slabs.name} name={slabs.name}>
                          {({ push, remove }) => (
                            <div className="mb-4">
                              {_.map(values[slabs.name], (row, index) => {
                                const path = (field) => `${slabs.name}.${index}.${field}`;
                                return (
                                  <div key={`${slabs.name}-${index}`} style={slabCard}>
                                    <div className="d-flex justify-content-between align-items-center mb-3">
                                      <div className="section-heading mb-0">
                                        {slabs.label} {index + 1}
                                      </div>
                                      {values[slabs.name].length > 1 && !isReadOnly && (
                                        <Button className="user-group-delete-Btn" onClick={() => remove(index)}>
                                          Remove
                                        </Button>
                                      )}
                                    </div>
                                    <Row>
                                      {_.map(slabs.prices, (field) => (
                                        <Col lg={3} key={field}>
                                          {input(path(field), PRICE_LABELS[field], { isRequired: true, type: 'number' })}
                                        </Col>
                                      ))}
                                      <Col lg={3}>
                                        <Select
                                          isRequired
                                          label="Discount Type"
                                          options={DISCOUNT_TYPE_OPTIONS}
                                          value={row.discount_type}
                                          isDisabled={isReadOnly}
                                          onChange={(val) => setFieldValue(path('discount_type'), val)}
                                        />
                                      </Col>
                                      <Col lg={3}>{input(path('discount_value'), 'Discount Value', { isRequired: true, type: 'number' })}</Col>
                                    </Row>
                                  </div>
                                );
                              })}
                              {!isReadOnly && (
                                <Button className="user-group-btn" onClick={() => push(slabs.empty)}>
                                  + Add {slabs.label}
                                </Button>
                              )}
                            </div>
                          )}
                        </FieldArray>

                        <div className="section-heading mb-3">App content</div>
                        <Row>
                          <Col lg={6}>{input('before_apply', 'Content Before Apply', { isRequired: true })}</Col>
                          <Col lg={6}>{input('after_apply', 'Content After Apply', { isRequired: true })}</Col>
                          <Col lg={12}>
                            <div style={{ fontSize: 12, opacity: 0.6, marginTop: -12 }}>{PLACEHOLDER_HELP}</div>
                          </Col>
                        </Row>
                      </fieldset>

                      <div className="d-flex justify-content-end mt-4" style={{ gap: 12 }}>
                        <Button className="cancel_btn" onClick={() => navigateTo('/sessionLevelOffers')}>
                          {t('button.cancel', 'Cancel')}
                        </Button>
                        {!isReadOnly && (
                          <Button type="submit" className="save-btn" disabled={isSubmitting}>
                            {isSubmitting ? 'Saving...' : isEdit ? t('button.update', 'Update') : t('button.save', 'Save')}
                          </Button>
                        )}
                      </div>
                    </Form>
                  );
                }}
              </Formik>
            </div>
          </Card>
        </div>
      </LayoutContainer>
    </React.Fragment>
  );
};

export default SessionLevelOfferForm;
